import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.97]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] hover:shadow-lg",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:scale-[1.02]",
        outline: "border-2 border-border bg-transparent text-foreground hover:bg-secondary hover:border-primary/50 hover:scale-[1.02]",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:scale-[1.02]",
        ghost: "hover:bg-secondary hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        glow: "bg-primary text-primary-foreground shadow-[0_0_25px_hsl(var(--primary)_/_0.4)] hover:shadow-[0_0_40px_hsl(var(--primary)_/_0.6)] hover:scale-[1.03]",
        accent: "bg-accent text-accent-foreground hover:bg-accent/90 hover:scale-[1.02]",
        "accent-glow": "bg-accent text-accent-foreground shadow-[0_0_25px_hsl(var(--accent)_/_0.4)] hover:shadow-[0_0_40px_hsl(var(--accent)_/_0.6)] hover:scale-[1.03]",
        success: "bg-success text-success-foreground hover:bg-success/90 hover:scale-[1.02]",
        premium: "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-[0_0_30px_hsl(var(--primary)_/_0.5)] hover:shadow-[0_0_50px_hsl(var(--primary)_/_0.7)] hover:scale-[1.04] font-bold",
        glass: "bg-white/5 backdrop-blur-xl border border-white/10 text-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_4px_16px_rgba(0,0,0,0.2)] hover:bg-white/10 hover:scale-[1.02]",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-lg px-4 text-xs",
        lg: "h-13 rounded-xl px-8 text-base",
        xl: "h-14 rounded-2xl px-10 text-lg font-bold",
        icon: "h-10 w-10",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
